<template>
  <!-- `--dias` manda cuántas columnas dibuja la grilla (antes eran 6 fijas en 6 reglas de CSS).
       Se define acá arriba y lo heredan .cabecera y .cuerpo-grid, incluidas las media queries. -->
  <div
    class="calendario-semanal"
    :class="{ arrastrando: turnoDragging }"
    :style="{ '--dias': diasSemana.length }"
  >
    <!-- HEADER CON NAVEGACIÓN -->
    <div class="header-semanal">
      <button @click="semanaAnterior" class="btn-nav">‹</button>
      <div class="semana-info">
        <span class="semana-rango">
          {{ primerDiaVisible.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) }} -
          {{ fechaFin.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) }}
        </span>
      </div>
      <button @click="semanaSiguiente" class="btn-nav">›</button>
    </div>

    <!-- TABLA SEMANAL -->
    <div class="tabla-semanal">
      <!-- CABECERA CON DÍAS -->
      <div class="cabecera">
        <div class="col-horas"></div>
        <div
          v-for="(día, idx) in diasSemana"
          :key="idx"
          class="col-dia"
          :class="{ 'col-dia-inactivo': día.inactivo }"
          :title="día.inactivo ? 'No atendés este día: quedan los turnos que ya tenías' : null"
        >
          <div class="dia-nombre">{{ día.nombre }}</div>
          <div class="dia-numero">{{ día.numero }}</div>
        </div>
      </div>

      <!-- CUERPO DEL CALENDARIO (GRID) -->
      <div class="cuerpo-grid">
        <!-- COLUMNA DE HORAS (Eje Y) -->
        <div class="columna-horas">
          <div
            v-for="hora in horas"
            :key="hora"
            class="celda-hora"
            :class="{ 'celda-hora-fuera': estaFueraDeHorario(hora) }"
            :title="estaFueraDeHorario(hora) ? 'Fuera de tu horario de trabajo' : null"
          >{{ hora }}</div>
        </div>

        <!-- COLUMNAS DE DÍAS (Eje X) -->
        <div
          v-for="(día, idx) in diasSemana"
          :key="idx"
          class="columna-dia"
          :class="{ 'columna-dia-inactiva': día.inactivo }"
        >
          <div
            v-for="hora in horas"
            :key="hora"
            class="celda-slot"
            :class="{ 'drag-over': slotDestino?.fecha === día.fecha && slotDestino?.hora === hora }"
            :data-fecha="día.fecha"
            :data-hora="hora"
            @click="abrirNuevoTurno(día.fecha, hora)"
          >
            <!-- Renderizar turno SOLO si empieza en esta hora -->
            <div
              v-if="obtenerTurno(día.fecha, hora)"
              class="evento"
              :class="[
                { 'dragging': turnoDragging?.id === obtenerTurno(día.fecha, hora)?.id },
                getDuracionMinutos(obtenerTurno(día.fecha, hora)) < 30 ? 'evento-corto' : ''
              ]"
              :style="getEstiloEvento(obtenerTurno(día.fecha, hora))"
              @pointerdown.stop="iniciarDrag(obtenerTurno(día.fecha, hora), $event)"
            >
              <!-- Turno corto (< 30 min): apilado compacto -->
              <template v-if="getDuracionMinutos(obtenerTurno(día.fecha, hora)) < 30">
                <div class="evento-hora-corto">{{ obtenerTurno(día.fecha, hora).inicio }}</div>
                <div class="evento-paciente-corto">{{ obtenerTurno(día.fecha, hora).paciente }}</div>
                <div class="evento-motivo-corto">{{ obtenerTurno(día.fecha, hora).motivo }}</div>
              </template>
              <!-- Turno normal (>= 30 min): layout apilado -->
              <template v-else>
                <div class="evento-hora">{{ obtenerTurno(día.fecha, hora).inicio }}</div>
                <div class="evento-paciente">{{ obtenerTurno(día.fecha, hora).paciente }}</div>
                <div class="evento-motivo">{{ obtenerTurno(día.fecha, hora).motivo }}</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useHorarioAgenda } from '@/composables/useHorarioAgenda';
import { DIAS_SEMANA } from '@/utils/horarios';

const { slotsPara, estaFueraDeHorario, esDiaActivo } = useHorarioAgenda();

const COLORES_ESTADO = {
  'pendiente':  '#678AB2',
  'confirmado': '#22c55e',
  'en curso':   '#f59e0b',
  'atendido':   '#a855f7',
  'no asiste':  '#92400e',
  'anulado':    '#ef4444',
  'bloqueo':    '#9ca3af',
};
function colorPorEstado(estado) {
  return COLORES_ESTADO[estado] || '#678AB2';
}

const emit = defineEmits(['seleccionarSlot', 'abrirTurno', 'moverTurno']);

const props = defineProps({
  turnos: {
    type: Array,
    default: () => []
  }
});

const fechaBase = ref(new Date());
const turnoDragging = ref(null);
const slotDestino = ref(null);

// Obtener el lunes de la semana actual
const fechaInicio = computed(() => {
  const fecha = new Date(fechaBase.value);
  const dia = fecha.getDay();
  const diff = fecha.getDate() - (dia === 0 ? 6 : dia - 1);
  return new Date(fecha.setDate(diff));
});

// Extremos de lo que se está mostrando, para el rótulo "3 ago - 7 ago" del encabezado.
// Ya no son "lunes" y "sábado" fijos: si el usuario atiende Mar–Vie, el rótulo dice Mar–Vie.
function diaConOffset(offset) {
  const fecha = new Date(fechaInicio.value);
  fecha.setDate(fecha.getDate() + offset);
  return fecha;
}

const primerDiaVisible = computed(() => diaConOffset(diasSemana.value[0]?.offset ?? 0));
const fechaFin = computed(() => diaConOffset(diasSemana.value.at(-1)?.offset ?? 5));

function fechaLocalStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Columnas de la grilla: solo los días que el usuario atiende, de lunes a domingo.
// Antes eran 6 fijas (Lun–Sáb): quien no trabajaba el sábado tenía una columna muerta y quien
// atendía el domingo no lo veía en ningún lado.
const diasSemana = computed(() => {
  const dias = [];
  for (let offset = 0; offset < 7; offset++) {
    const fecha = new Date(fechaInicio.value);
    fecha.setDate(fecha.getDate() + offset);
    const fechaStr = fechaLocalStr(fecha);
    const activo = esDiaActivo(fecha);
    // Un día apagado se oculta SOLO si está vacío. Si tiene turnos (por ejemplo, se apagaron
    // los lunes después de haber agendado uno), la columna se muestra igual, en gris: esos
    // pacientes tienen que seguir a la vista hasta que se los mueva o se los borre.
    const tieneTurnos = props.turnos.some(
      (t) => t.estado !== 'eliminada' && String(t.fecha).split('T')[0] === fechaStr
    );
    if (!activo && !tieneTurnos) continue;
    dias.push({
      nombre: DIAS_SEMANA[offset].label,
      numero: fecha.getDate(),
      fecha: fechaStr,
      offset,
      inactivo: !activo,
    });
  }
  return dias;
});

// Filas de la grilla: el horario de trabajo configurado en Ajustes (por defecto 09:00 a 22:30).
// Se pasan SOLO los turnos de la semana visible: si se pasara la colección entera, cualquier
// turno viejo a una hora rara agregaría una fila a todas las semanas.
const horas = computed(() => {
  const fechasSemana = new Set(diasSemana.value.map((d) => d.fecha));
  const turnosSemana = props.turnos.filter(
    (t) => t.estado !== 'eliminada' && fechasSemana.has(String(t.fecha).split('T')[0])
  );
  return slotsPara(turnosSemana);
});

// Obtener turno en una fecha y hora específica
function obtenerTurno(fecha, hora) {
  return props.turnos.find(t => {
    const turnoFecha = t.fecha.split('T')[0];
    return turnoFecha === fecha && t.inicio === hora && t.estado !== 'eliminada';
  });
}

// Navegación de semanas
function semanaAnterior() {
  fechaBase.value = new Date(fechaBase.value.setDate(fechaBase.value.getDate() - 7));
}

function semanaSiguiente() {
  fechaBase.value = new Date(fechaBase.value.setDate(fechaBase.value.getDate() + 7));
}

function abrirNuevoTurno(fecha, hora) {
  if (obtenerTurno(fecha, hora)) return;
  // La columna de un día apagado solo está para ver los turnos que ya tenía: no se agenda ahí.
  if (!esDiaActivo(fecha)) return;
  emit('seleccionarSlot', { fecha, hora });
}

function abrirDetallesTurno(turno) {
  emit('abrirTurno', turno);
}

function getDuracionMinutos(turno) {
  if (!turno?.inicio || !turno?.fin) return 30;
  const [hInicio, mInicio] = turno.inicio.split(':').map(Number);
  const [hFin, mFin] = turno.fin.split(':').map(Number);
  return (hFin * 60 + mFin) - (hInicio * 60 + mInicio);
}

// Calcular altura del evento basado en duración
function getEstiloEvento(turno) {
  if (!turno) return {};

  const [hInicio, mInicio] = turno.inicio.split(':').map(Number);
  const [hFin, mFin] = turno.fin.split(':').map(Number);

  const minutosInicio = hInicio * 60 + mInicio;
  const minutosFin = hFin * 60 + mFin;
  const duracionMinutos = minutosFin - minutosInicio;
  const slots = duracionMinutos / 30;

  return {
    backgroundColor: colorPorEstado(turno.estado),
    height: `calc(${slots * 100}% + ${(slots - 1) * 2}px)`,
    zIndex: 10
  };
}

// DRAG & DROP
function iniciarDrag(turno, event) {
  event.preventDefault();

  const startX = event.clientX;
  const startY = event.clientY;
  let dragging = false;

  const onMove = (e) => {
    const dx = Math.abs(e.clientX - startX);
    const dy = Math.abs(e.clientY - startY);

    if (!dragging && (dx > 5 || dy > 5)) {
      dragging = true;
      turnoDragging.value = turno;
    }

    if (!dragging) return;

    // elementFromPoint funciona porque .dragging tiene pointer-events: none
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const celda = el?.closest('[data-fecha][data-hora]');
    if (celda) {
      slotDestino.value = {
        fecha: celda.dataset.fecha,
        hora: celda.dataset.hora
      };
    }
  };

  const onUp = () => {
    if (dragging && slotDestino.value) {
      const { fecha, hora } = slotDestino.value;
      const turnoFecha = turno.fecha.split('T')[0];
      // Arrastrar hacia un día apagado no mueve nada: si no, se podría meter un turno en un
      // día donde el modal de agendar no te dejaría guardarlo. Se permite moverlo DENTRO del
      // mismo día apagado (cambiarle la hora a uno que ya estaba ahí).
      const destinoValido = esDiaActivo(fecha) || fecha === turnoFecha;
      if (destinoValido && (fecha !== turnoFecha || hora !== turno.inicio)) {
        emit('moverTurno', { id: turno.id, fecha, hora });
      }
    } else if (!dragging) {
      abrirDetallesTurno(turno);
    }

    turnoDragging.value = null;
    slotDestino.value = null;
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
  };

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
}
</script>

<style scoped>
.calendario-semanal {
  padding: 12px;
  background: white;
}

.header-semanal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px 0;
  border-bottom: 2px solid #eee;
}

.btn-nav {
  background: none;
  border: none;
  font-size: 24px;
  color: #678AB2;
  cursor: pointer;
  padding: 0 8px;
}

.semana-rango {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.tabla-semanal {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
}

.cabecera {
  display: grid;
  grid-template-columns: 50px repeat(var(--dias, 6), 1fr);
  gap: 4px;
  margin-bottom: 8px;
  background: #f9f9f9;
  border-radius: 6px;
  padding: 8px 4px;
}

.col-horas {
  min-width: 50px;
}

.col-dia {
  text-align: center;
}

.dia-nombre {
  font-weight: 600;
  color: #666;
  font-size: 10px;
  text-transform: uppercase;
}

.dia-numero {
  font-weight: 700;
  color: #678AB2;
  font-size: 14px;
  margin-top: 2px;
}

/* El cuerpo tiene que repetir EXACTAMENTE las tres medidas de `.cabecera` —columnas, gap y
   padding horizontal— o los nombres de los días no caen sobre sus columnas. Ya coincidían las
   columnas, pero la cabecera tenía además `padding: … 4px` y en tablet/escritorio sube el gap
   a 6px y 8px, cosa que el cuerpo no seguía: eso corría los días respecto de la grilla, y cada
   vez más hacia la derecha. Si cambiás una de las tres, cambiá la otra. */
.cuerpo-grid {
  display: grid;
  grid-template-columns: 50px repeat(var(--dias, 6), 1fr);
  gap: 4px;
  padding: 0 4px;
}

.columna-horas {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* `box-sizing: border-box` acá NO es cosmético: es lo que mantiene alineada la grilla.
   No hay reset global de box-sizing en el proyecto, así que rige `content-box` y el padding
   se SUMA al height. Esta celda tiene `padding-top` y la de al lado no, con lo cual cada fila
   de horas medía 4px más que su fila de casillas y la etiqueta se iba corriendo hacia abajo,
   fila a fila, hasta quedar más de una fila por debajo de su cuadrado al final del día.
   Con border-box las dos miden exactamente lo mismo. Si se toca el padding de una, hay que
   mirar la otra. */
.celda-hora {
  box-sizing: border-box;
  height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  font-weight: 600;
  color: #678AB2;
  font-size: 10px;
  padding-right: 4px;
  padding-top: 4px;
  border-top: 1px solid transparent;
}

/* Fila que quedó fuera del horario configurado: existe solo porque hay un turno ahí
   (típicamente importado de Google Calendar). Se marca en ámbar para que se entienda. */
.celda-hora-fuera {
  color: #b45309;
}

/* Columna de un día apagado que sigue a la vista porque tiene turnos. Se ve gris para que
   se entienda que no se agenda ahí, pero los turnos que ya estaban se leen normal. */
.col-dia-inactivo {
  color: #9ca3af;
}

.columna-dia-inactiva {
  background: #f1f2f4;
}

.columna-dia {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.celda-slot {
  /* Ver la nota de `.celda-hora`: las dos tienen que medir igual o la grilla se desalinea. */
  box-sizing: border-box;
  height: 60px;
  position: relative;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  border-top: 1px solid #eee;
  transition: background 0.15s;
}

.celda-slot.drag-over {
  background: #dce8f5;
  border: 2px dashed #678AB2;
}

.evento {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;
  color: white;
  padding: 4px;
  border-radius: 3px;
  font-size: 9px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-shadow: 0 2px 6px rgba(103, 138, 178, 0.3);
  overflow: hidden;
  pointer-events: auto;
  cursor: grab;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  touch-action: none;
}

.evento:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(103, 138, 178, 0.5);
}

.evento.dragging {
  opacity: 0.45;
  pointer-events: none;
  cursor: grabbing;
  transform: scale(1.04);
}

/* Mientras se arrastra, NINGUNA cita intercepta el cursor: así el destino del drop
   es siempre la celda real bajo el dedo y no el inicio de una cita larga (que tapa
   visualmente los slots de abajo). */
.arrastrando .evento {
  pointer-events: none;
}

.evento-hora {
  font-weight: 700;
  font-size: 10px;
}

.evento-paciente {
  font-weight: 600;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.evento-motivo {
  font-size: 8px;
  opacity: 0.9;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Evento corto (< 30 min): apilado compacto */
.evento-corto {
  padding: 2px 3px;
  gap: 1px;
}

.evento-hora-corto {
  font-weight: 700;
  font-size: 9px;
  line-height: 1.1;
}

.evento-paciente-corto {
  font-weight: 600;
  font-size: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

.evento-motivo-corto {
  font-size: 7px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

/* Responsive: tablet */
@media (min-width: 768px) {
  .evento-hora-corto {
    font-size: 9px;
  }
  .evento-paciente-corto {
    font-size: 9px;
  }
  .evento-motivo-corto {
    font-size: 8px;
  }
  .evento-corto {
    padding: 3px 4px;
    gap: 2px;
  }
}

/* Responsive: desktop */
@media (min-width: 1024px) {
  .evento-hora-corto {
    font-size: 10px;
  }
  .evento-paciente-corto {
    font-size: 10px;
  }
  .evento-motivo-corto {
    font-size: 9px;
  }
  .evento-corto {
    padding: 3px 5px;
    gap: 2px;
  }
}

/* Mobile: scroll horizontal con columnas mínimas para leer el texto */
@media (max-width: 767px) {
  .cabecera,
  .cuerpo-grid {
    grid-template-columns: 40px repeat(var(--dias, 6), minmax(70px, 1fr));
    min-width: 460px;
  }

  .col-horas {
    min-width: 40px;
  }
}

/* Tablet */
@media (min-width: 768px) {
  .calendario-semanal {
    padding: 16px;
  }

  .header-semanal {
    margin-bottom: 20px;
  }

  .semana-rango {
    font-size: 15px;
  }

  .cabecera {
    grid-template-columns: 60px repeat(var(--dias, 6), 1fr);
    gap: 6px;
    padding: 10px 6px;
  }

  .col-horas {
    min-width: 60px;
  }

  .dia-numero {
    font-size: 16px;
  }

  .cuerpo-grid {
    grid-template-columns: 60px repeat(var(--dias, 6), 1fr);
    gap: 6px;
    padding: 0 6px;
  }

  .celda-hora, .celda-slot {
    height: 70px;
    font-size: 12px;
  }

  .evento {
    font-size: 10px;
    padding: 5px;
  }

  .evento-hora {
    font-size: 11px;
  }

  .evento-motivo {
    font-size: 9px;
  }

  .evento-corto {
    padding: 2px 3px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .calendario-semanal {
    padding: 20px;
  }

  .header-semanal {
    margin-bottom: 24px;
    padding: 16px 0;
  }

  .btn-nav {
    padding: 0 16px;
  }

  .semana-rango {
    font-size: 18px;
  }

  .cabecera {
    grid-template-columns: 80px repeat(var(--dias, 6), 1fr);
    gap: 8px;
    padding: 12px 8px;
  }

  .col-horas {
    min-width: 80px;
  }

  .dia-nombre {
    font-size: 12px;
  }

  .dia-numero {
    font-size: 18px;
    margin-top: 4px;
  }

  .cuerpo-grid {
    grid-template-columns: 80px repeat(var(--dias, 6), 1fr);
    gap: 8px;
    padding: 0 8px;
  }

  .celda-hora, .celda-slot {
    height: 80px;
    font-size: 12px;
    padding-top: 8px;
  }

  .evento {
    font-size: 11px;
    padding: 6px;
  }

  .evento-hora {
    font-size: 12px;
  }

  .evento-motivo {
    font-size: 10px;
  }

  .evento-corto {
    padding: 2px 4px;
  }
}

/* Fix: Asegurar que la navegación inferior esté siempre por encima de los eventos */
:global(.bottom) {
  z-index: 1000 !important;
  position: relative;
}
</style>
